import { useState, useEffect } from "react";
import { isEmbroideryTrace } from "../../config/appVariant";
import { IonPage, IonContent, IonSpinner } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { createCheckoutSession } from "../../lib/stripe/createCheckoutSession";
import { trackViewContent, trackInitiateCheckout } from "../../lib/analytics/metaPixel";
import logo from "../../assets/logo1.svg";
import "./WelcomeBack.css";

const BENEFITS = [
  { icon: "⬇️", text: "Unlimited SVG exports" },
  { icon: "🧵", text: "Full Access to Embroidery Editor" },
  { icon: "🎨", text: "Commercial License" },
  { icon: "🔄", text: "AI Retrace & Modify" },
  { icon: "✂️", text: "Remove Background" },
  { icon: "📦", text: "Bulk Convert" },
  ...(!isEmbroideryTrace ? [{ icon: "✨", text: "AI Image Generator" }] : []),
];

const WelcomeBack = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, isLoading: authLoading } = useAuth();
  const history = useHistory();

  useEffect(() => {
    trackViewContent("welcome-back");
  }, []);

  useEffect(() => {
    const handleError = (e) => {
      setError(e.detail?.error || "Something went wrong. Please try again.");
      setLoading(false);
    };
    window.addEventListener("stripe-checkout-error", handleError);
    return () => window.removeEventListener("stripe-checkout-error", handleError);
  }, []);

  const handleCheckout = async () => {
    if (!user) {
      history.push("/signin?redirect=/welcome-back");
      return;
    }

    setLoading(true);
    setError(null);
    trackInitiateCheckout("designerYearly");

    try {
      await createCheckoutSession(user.uid, "designerYearly", {
        coupon: "WELCOME50",
        trialFromPlan: false,
      });
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <IonPage>
        <IonContent className="wb-background">
          <div className="wb-loading">
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent className="wb-background">
        <div className="wb-container">
          <div className="wb-card">
            <div className="wb-logo">
              <img src={logo} alt="SvgTrace" />
            </div>

            <div className="wb-offer-badge">LIMITED OFFER</div>

            <h1 className="wb-title">Welcome Back!</h1>
            <p className="wb-subtitle">
              You're one step away from unlimited SVG and embroidery exports.
            </p>

            <div className="wb-price-box">
              <div className="wb-price-row">
                <span className="wb-price-original">$95.88/yr</span>
                <span className="wb-price-arrow">→</span>
                <span className="wb-price-new">$47.00/yr</span>
              </div>
              <div className="wb-price-savings">Save $48.88 on your first year</div>
              <div className="wb-price-monthly">That's just $3.92/mo</div>
            </div>

            <div className="wb-benefits">
              {BENEFITS.map((b) => (
                <div className="wb-benefit" key={b.text}>
                  <span className="wb-benefit-icon">{b.icon}</span>
                  <span className="wb-benefit-text">{b.text}</span>
                </div>
              ))}
            </div>

            {error && <div className="wb-error">{error}</div>}

            <button
              className="wb-cta"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? (
                <IonSpinner name="lines" />
              ) : user ? (
                "Claim Your Discount — $47.00/yr"
              ) : (
                "Sign In to Claim Your Discount"
              )}
            </button>

            <div className="wb-trust">
              <span>✓ Cancel anytime</span>
              <span>✓ 30-day money-back guarantee</span>
              <span>🔒 Secure checkout</span>
            </div>

            <p className="wb-footnote">
              $48.88 discount applies to the first year of the annual plan.
              Regular price ($95.88/yr) applies at renewal.
            </p>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default WelcomeBack;
